/**
 * Language switcher — flag images (flagcdn) and solid white surfaces, aligned with DealCouponz/client.
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ChevronDown } from "lucide-react";
import languageConfig from "../../config/languageConfig";
import { changeLanguage } from "../../i18n/config";
import { getLanguageFromUrl } from "../../utils/hreflangGenerator";

const getFlagUrl = (code) => {
  const flagMap = {
    en: "https://flagcdn.com/us.svg",
    "en-GB": "https://flagcdn.com/gb.svg",
    "en-AU": "https://flagcdn.com/au.svg",
    ga: "https://flagcdn.com/ie.svg",
    de: "https://flagcdn.com/de.svg",
    "de-AT": "https://flagcdn.com/at.svg",
    es: "https://flagcdn.com/es.svg",
    it: "https://flagcdn.com/it.svg",
    no: "https://flagcdn.com/no.svg",
    fi: "https://flagcdn.com/fi.svg",
    da: "https://flagcdn.com/dk.svg",
    sv: "https://flagcdn.com/se.svg",
    fr: "https://flagcdn.com/fr.svg",
    pt: "https://flagcdn.com/pt.svg",
    nl: "https://flagcdn.com/nl.svg",
  };
  return flagMap[code] || "https://flagcdn.com/w320/un.png";
};

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  z-index: 50;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  min-width: 140px;
  border: 1px solid ${({ theme }) => theme?.colors?.border || "#e5e7eb"};
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text || "#1e293b"};
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background: #f9fafb;
    border-color: ${({ theme }) => theme?.colors?.primary || "#ca8a04"};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }
`;

const TriggerMain = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const FlagSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 21px;
  flex-shrink: 0;
`;

const FlagImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2px;
  display: block;
`;

const LangLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronWrap = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme?.colors?.mutedForeground || "#64748b"};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme?.colors?.border || "#e5e7eb"};
  border-radius: 12px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.08),
    0 4px 6px -2px rgba(0, 0, 0, 0.04);
  z-index: 10001;
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
`;

const LangItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  background: ${({ $active }) => ($active ? "#eff6ff" : "#ffffff")};
  border: 1px solid ${({ $active }) => ($active ? "#3b82f6" : "transparent")};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 0.9375rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ theme }) => theme?.colors?.text || "#1e293b"};
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: ${({ $active }) => ($active ? "#dbeafe" : "#f9fafb")};
    border-color: ${({ $active }) => ($active ? "#3b82f6" : "#e5e7eb")};
  }
`;

const OptionFlagSlot = styled.span`
  display: inline-flex;
  width: 36px;
  height: 27px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`;

const CheckMark = styled.span`
  margin-left: auto;
  color: #2563eb;
  font-weight: 700;
  font-size: 1rem;
`;

const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentLangCode = getLanguageFromUrl(location.pathname);
  const currentLang =
    languageConfig.getLanguage(currentLangCode) || languageConfig.getLanguage("en");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectLanguage = async (lang) => {
    setOpen(false);
    if (lang.code === currentLangCode) return;

    await changeLanguage(lang.code, false);
    localStorage.setItem("preferredLanguage", lang.code);

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const urlCodes = languageConfig.getUrlCodes();
    let pathWithoutLang = location.pathname;

    if (pathSegments.length > 0 && urlCodes.includes(pathSegments[0])) {
      pathSegments.shift();
      pathWithoutLang = pathSegments.length > 0 ? `/${pathSegments.join("/")}` : "/";
    }

    let newPath;
    if (lang.code === languageConfig.defaultLanguage) {
      newPath = pathWithoutLang || "/";
    } else {
      const newUrlCode = languageConfig.getUrlCode(lang.code);
      newPath = `/${newUrlCode}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    }

    navigate(newPath, { replace: true });
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger type="button" onClick={() => setOpen((prev) => !prev)} aria-label="Switch language">
        <TriggerMain>
          <FlagSlot title={currentLang?.name}>
            <FlagImg src={getFlagUrl(currentLangCode)} alt="" />
          </FlagSlot>
          <LangLabel>{currentLang?.nativeName || currentLang?.name}</LangLabel>
        </TriggerMain>
        <ChevronWrap $open={open} aria-hidden>
          <ChevronDown size={14} strokeWidth={2.25} />
        </ChevronWrap>
      </Trigger>

      {open && (
        <Dropdown role="listbox">
          {languageConfig.supportedLanguages.map((lang) => (
            <LangItem
              key={lang.code}
              type="button"
              role="option"
              aria-selected={lang.code === currentLangCode}
              $active={lang.code === currentLangCode}
              onClick={() => handleSelectLanguage(lang)}
            >
              <OptionFlagSlot>
                <FlagImg src={getFlagUrl(lang.code)} alt="" />
              </OptionFlagSlot>
              <span>{lang.nativeName || lang.name}</span>
              {lang.code === currentLangCode ? <CheckMark>✓</CheckMark> : null}
            </LangItem>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default LanguageSwitcher;
